import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Dropdown, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ENotificationType } from '../../../../../model/campaign/Campaign';
import { useSearchQuery } from '../../../../../shared/hooks/useSearchQuery';
import { gtmService } from '../../../../../utils/gtm.service';
import { InfluencerProfileInfo } from './components/InfluencerProfileInfo';
import { OfferModalTabs } from './tabs';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import {
    calcFee,
    formatDate,
    getGrandTotal,
    getNumberContentPieces,
    getNumberPosts,
    getStatusIcon,
    getSubtotalAdditionalItems,
    getSubtotalOfferItems,
} from '../../../../../shared/functions/Functions';
import {
    setActiveTab,
    setCampaignId,
    setInitialState,
    setOfferModalDetails,
    setOfferModalStatus,
} from '../../../../../store/influencer/offerInfluencerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { ManagementOfferService } from '../../service';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { IAllStates } from '../../../../../store/rootReducer';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { scrollToTopRef } from '../../../../../utils/helpers/ScrollToTop';
import { scrollToBottomRef } from '../../../../../utils/helpers/scrollToBottomRef';
import { ConfirmSendingOfferModal } from './components/ConfirmSendingOfferModal';
import { DeclineOfferModal } from './components/DeclineOfferModal';
import { IDeclineOffer } from '../../dto/IDeclineOffer';
import { FiInfo } from 'react-icons/fi';
import { InfluencerAcceptOfferModal } from './components/InfluencerAcceptOfferModal';
import HtmlToPdf from '../../../../../shared/components/generate-pdf';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { OfferPdfDownloadButton } from '../../../../../shared/components/pdf/DownloadOfferPdf';
import { CrossModalIcon, WithdrawIcon } from '../../../../../shared/Icons';

interface InfluencerOfferModalProps {
    show: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    campaignId: string;
    isLongTerm: boolean;
    updateList?: React.Dispatch<React.SetStateAction<boolean>>;
    setIsRocketShown?: React.Dispatch<React.SetStateAction<boolean>>;
}

export function InfluencerOfferModal({
    show,
    setShowModal,
    campaignId,
    isLongTerm = false,
    setIsRocketShown,
    updateList,
}: InfluencerOfferModalProps) {
    const dispatch = useDispatch();
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [disableButton, setDisableButton] = useState(false);
    const [declineReasons, setDeclineReasons] = useState<IDeclineOffer>({ declineReason: '', declineComment: '' });
    const offerModalRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation();
    const {
        campaign,
        activeTabModal,
        initialOffer,
        isEditable,
        canCounter,
        offerModalStatus,
        hasLegal,
        isCharity,
        isLegalChecked,
        isDistributionChecked,
        requiredFieldChangeColor,
        hasFee,
        feePercentage,
        inviteAttachment,
    } = useSelector((state: IAllStates) => state.offerInfluencer);

    const navigate = useNavigate();
    const notificationQueryType = useSearchQuery('notificationType');

    const additionalItemsSum = useMemo(
        () => getSubtotalAdditionalItems(initialOffer.distribution),
        [initialOffer.distribution]
    );

    const offerItemsSum = useMemo(() => getSubtotalOfferItems(initialOffer.offerItems), [initialOffer.offerItems]);

    const { grandTotalSum } = useMemo(() => {
        const baseTotal = additionalItemsSum + offerItemsSum;
        const fee = hasFee ? calcFee(baseTotal, feePercentage) : 0;
        return {
            grandTotalSum: priceSplitter(baseTotal - Math.round(fee)),
        };
    }, [additionalItemsSum, offerItemsSum, hasFee, feePercentage]);

    useEffect(() => {
        show &&
            ManagementOfferService.getOfferModalDetail(
                campaignId,
                notificationQueryType === ENotificationType?.OFFER_LONG ? true : isLongTerm
            )
                .then(response => {
                    notificationQueryType?.length && navigate('/influencer/offers');
                    dispatch(
                        setOfferModalDetails({
                            ...response,
                            activeTabModal: notificationQueryType === ENotificationType.OFFER_COMMENT ? 5 : undefined,
                        })
                    );
                    dispatch(setCampaignId(campaignId));
                })
                .catch(err => {
                    closeModal();
                    ErrorToast(err);
                });
    }, [show]);

    const sendInitialOfferRequest = () => {
        setDisableButton(true);
        let initialOfferCopy = initialOffer;
        const offerItems = isCharity
            ? initialOfferCopy.offerItems
            : initialOfferCopy.offerItems.filter(item => item.nbPosts > 0 && item.price > 0);
        const price = getGrandTotal(initialOfferCopy.offerItems, initialOfferCopy.distribution);
        initialOfferCopy = { ...initialOfferCopy, offerItems, price };

        const formData = new FormData();
        const realFiles = Array.isArray(inviteAttachment) ? inviteAttachment.filter(item => item instanceof Blob) : [];
        const hasPlaceholder = Array.isArray(inviteAttachment)
            ? inviteAttachment.some(item => !(item instanceof Blob))
            : false;
        const isFileCleared = realFiles.length === 0 && !hasPlaceholder;

        formData.append(
            'body',
            JSON.stringify({
                ...initialOfferCopy,
                ...(isFileCleared && { clearLegal: true }),
            })
        );

        if (realFiles.length > 0) {
            for (const item of realFiles) {
                formData.append('file', item as Blob);
            }
        }

        ManagementOfferService.sendInitialOffer(campaignId, formData)
            .then(() => {
                gtmService('influencer_offer_submitted', grandTotalSum);
                SuccessToast(t('influencer.offer_management.modal.success_initial_message'));
                setDisableButton(false);
                closeModal();
            })
            .catch(err => {
                ErrorToast(err);
                setDisableButton(false);
            });
    };

    const sendCounterOfferRequest = () => {
        setDisableButton(true);
        let initialOfferCopy = initialOffer;
        const offerItems = isCharity
            ? initialOfferCopy.offerItems
            : initialOfferCopy.offerItems.filter(item => item.nbPosts > 0 && item.price > 0);
        const price = getGrandTotal(initialOfferCopy.offerItems, initialOfferCopy.distribution);
        initialOfferCopy = { ...initialOfferCopy, offerItems, price };

        const formData = new FormData();
        const realFiles = Array.isArray(inviteAttachment) ? inviteAttachment.filter(item => item instanceof Blob) : [];
        const hasPlaceholder = Array.isArray(inviteAttachment)
            ? inviteAttachment.some(item => !(item instanceof Blob))
            : false;
        const isFileCleared = realFiles.length === 0 && !hasPlaceholder;

        formData.append(
            'body',
            JSON.stringify({
                ...initialOfferCopy,
                ...(isFileCleared && { clearLegal: true }),
            })
        );

        if (realFiles.length > 0) {
            for (const item of realFiles) {
                formData.append('file', item as Blob);
            }
        }

        ManagementOfferService.sendCounterOffer(campaignId, formData)
            .then(() => {
                SuccessToast(t('influencer.offer_management.modal.success_update_message'));
                gtmService('influencer_offer_submitted', grandTotalSum);
                setDisableButton(false);
                closeModal();
            })
            .catch(err => {
                ErrorToast(err);
                setDisableButton(false);
            });
    };
    const setNextTab = () => {
        let currentStep;
        if (!hasLegal && activeTabModal === 2) {
            currentStep = activeTabModal + 2;
        } else {
            currentStep = activeTabModal + 1;
        }
        if (requiredFieldChangeColor && activeTabModal === 1) {
            scrollToTopRef(offerModalRef);
        } else if (!isLegalChecked && activeTabModal === 3) {
            scrollToBottomRef(offerModalRef);
        } else if (!isDistributionChecked && activeTabModal === 2) {
            scrollToBottomRef(offerModalRef);
        } else {
            scrollToTopRef(offerModalRef);
        }
        dispatch(setActiveTab(currentStep));
    };

    const acceptOffer = () => {
        ManagementOfferService.acceptOffer(campaignId)
            .then(() => {
                gtmService('influencer_offer_accepted');
                setIsRocketShown && setIsRocketShown(true);
                SuccessToast(t('influencer.offer_management.modal.success_accepted_message'));
                closeModal();
            })
            .catch(err => ErrorToast(err));
    };
    const withdrawOffer = () => {
        ManagementOfferService.withdrawOffer(campaignId)
            .then(() => {
                SuccessToast(t('influencer.offer_management.modal.success_withdraw_message'));
                closeModal();
            })
            .catch(err => ErrorToast(err));
    };
    const declineOffer = () => {
        ManagementOfferService.declineOffer(campaignId, declineReasons)
            .then(() => {
                SuccessToast(t('influencer.offer_management.modal.success_decline_message'));
                closeModal();
            })
            .catch(err => ErrorToast(err));
    };

    const changeStatus = () => {
        dispatch(setActiveTab(4));
        dispatch(setOfferModalStatus('negotiate'));
    };
    const closeAcceptModal = () => {
        setShowAcceptModal(false);
    };
    const closeSubmitModal = () => {
        setShowSubmitModal(false);
    };
    const closeDeclineModal = () => {
        setShowDeclineModal(false);
    };

    const closeModal = () => {
        dispatch(setInitialState());
        setShowModal(false);
        if (updateList) {
            updateList(prevState => !prevState);
        }
    };

    const openSubmitConfirmModal = (event: any) => {
        event.preventDefault();
        setShowSubmitModal(true);
    };

    return (
        <Modal
            show={show}
            onHide={closeModal}
            size="xl"
            id="offer-modal"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <form onSubmit={e => openSubmitConfirmModal(e)}>
                <Modal.Body
                    className={`${
                        canCounter || campaign?.offers[0]?.status === 'negotiate'
                            ? 'negotiate-status-mobile'
                            : `border-2 border-${campaign?.offers[0]?.status}-color-mobile border-bottom-0`
                    }`}
                >
                    {campaign?.offers[0]?.id && (
                        <div className={`offer-status-badge ${campaign?.offers[0]?.status} d-none d-md-flex`}>
                            {getStatusIcon(campaign?.offers[0]?.status ?? '')}
                            <p>{t(`general.offerStatus.${campaign?.offers[0]?.status}`)}</p>
                        </div>
                    )}
                    <Row className="pb-4 h-100">
                        <Col xl={3} lg={12} className="profile-info">
                            <InfluencerProfileInfo />
                        </Col>
                        <Col xl={9} lg={12} style={{ height: 'calc(100% - 0px)' }}>
                            <OfferModalTabs offerModalRef={offerModalRef} />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="position-relative bg-white shadow-top">
                    <Row className="justify-content-between align-items-center w-100">
                        <Col xl={6} className="col-auto d-flex">
                            <div className="d-md-flex d-none align-items-center">
                                <CustomImageHandler
                                    photoPath={campaign?.company?.profilePhoto?.thumbnail ?? ''}
                                    altTag="User Profile"
                                    classes="middle-rounded-logo"
                                    name={campaign?.company?.displayName}
                                />
                                <h6 className="mb-0 ml-2">{campaign?.company?.displayName}</h6>
                            </div>
                            <div className="d-md-flex d-none align-items-center ml-3">
                                <h6 className="text-primary mb-0">{t('influencer.offers.invoicing_disclaimer')}</h6>
                                <TooltipOverlay
                                    className="ms-1"
                                    content={
                                        <p
                                            className="m-0"
                                            dangerouslySetInnerHTML={{
                                                __html: t(`influencer.offers.invoicing_disclaimer_tooltip`),
                                            }}
                                        ></p>
                                    }
                                >
                                    <FiInfo size={14} className="text-primary" />
                                </TooltipOverlay>
                            </div>
                            {activeTabModal === 4 ? (
                                <div className="d-block text-uppercase d-md-none mb-2">
                                    <div className="d-flex align-items-top">
                                        {campaign?.isCharity ? (
                                            <>
                                                {+grandTotalSum > 0 ? (
                                                    <h5 className="text-primary mb-0 euro-symbol">{grandTotalSum}</h5>
                                                ) : (
                                                    <h5 className="text-primary-dark m-0 font-14">
                                                        {t('general.button.barter_deal')}
                                                    </h5>
                                                )}
                                            </>
                                        ) : (
                                            <h5 className="text-primary mb-0 euro-symbol">{grandTotalSum}</h5>
                                        )}
                                        {isCharity && (
                                            <TooltipOverlay
                                                className="ms-2"
                                                content={t(`influencer.explore.barterTooltip`)}
                                            >
                                                <FiInfo size={14} />
                                            </TooltipOverlay>
                                        )}
                                    </div>
                                    <h6 className="mb-0 mt-1 font-11">
                                        {t('brand.campaign.book.offer.modal.grand_total')}
                                    </h6>
                                </div>
                            ) : null}
                        </Col>
                        <Col
                            md={12}
                            xl={6}
                            className="d-flex align-items-center justify-content-between justify-content-md-end col-auto gap-3"
                        >
                            <OfferPdfDownloadButton
                                offerId={campaign?.offers[0]?.id}
                                status={campaign?.offers[0]?.status}
                                isZip={true}
                            />
                            {/*{(campaign?.offers[0]?.status === 'accepted' ||*/}
                            {/*    campaign?.offers[0]?.status === 'completed') && (*/}
                            {/*    <HtmlToPdf*/}
                            {/*        styleContent="mr-4"*/}
                            {/*        passedOffers={campaign?.offers[0].id}*/}
                            {/*        passedCampaignId={+campaignId}*/}
                            {/*    />*/}
                            {/*)}*/}
                            {activeTabModal === 4 && (
                                <div className="align-items-center mr-md-4 mr-2 d-none d-md-flex text-uppercase">
                                    {isCharity && (
                                        <TooltipOverlay
                                            className="ms-2"
                                            content={t(`influencer.explore.barterTooltip`)}
                                        >
                                            <FiInfo size={14} />
                                        </TooltipOverlay>
                                    )}
                                    <h6 className="mb-0 mx-1">{t('brand.campaign.book.offer.modal.grand_total')}:</h6>
                                    <div className="d-flex align-items-top">
                                        {campaign?.isCharity ? (
                                            <>
                                                {+grandTotalSum > 0 ? (
                                                    <h5 className="text-primary mb-0 euro-symbol">{grandTotalSum}</h5>
                                                ) : (
                                                    <h5 className="text-primary-dark m-0 font-14">
                                                        {t('general.button.barter_deal')}
                                                    </h5>
                                                )}
                                            </>
                                        ) : (
                                            <h5 className="text-primary mb-0 euro-symbol">{grandTotalSum}</h5>
                                        )}
                                    </div>
                                </div>
                            )}
                            <Button
                                onClick={closeModal}
                                className="text-uppercase"
                                variant="outline-secondary"
                                size="sm"
                            >
                                {t('general.button.close')}
                            </Button>
                            <div className="d-flex">
                                {(!campaign?.offerPlaceholder?.flags?.canWithdraw && !canCounter) ||
                                offerModalStatus === 'negotiate' ? null : (
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            className="mr-2 btn-outline-primary btn-sm"
                                            variant="light"
                                            id="dropdown-basic"
                                        >
                                            {t('general.button.respond')}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {isEditable && (
                                                <Dropdown.Item id="negotiate" onClick={changeStatus}>
                                                    <div className="d-flex align-items-center">
                                                        <i className="fi fi-rr-messages-question icon-fixed me-2"></i>
                                                        {t('general.button.negotiate')}
                                                    </div>
                                                </Dropdown.Item>
                                            )}
                                            {canCounter && (
                                                <Dropdown.Item
                                                    id="decline"
                                                    onClick={() => setShowDeclineModal(true)}
                                                    className="d-flex align-items-center danger-hover"
                                                >
                                                    <CrossModalIcon width={16} height={16} className="me-2" />
                                                    {t('general.button.decline')}
                                                </Dropdown.Item>
                                            )}
                                            {campaign?.offerPlaceholder?.flags?.canWithdraw && (
                                                <Dropdown.Item id="withdraw" onClick={withdrawOffer}>
                                                    <div className="d-flex align-items-center">
                                                        <WithdrawIcon width={16} height={16} className="me-2" />
                                                        {t('brand.campaign.book.offer.modal.withdraw_offer')}
                                                    </div>
                                                </Dropdown.Item>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )}
                                <Button
                                    onClick={setNextTab}
                                    className={`${activeTabModal >= 4 ? 'd-none' : ''}`}
                                    variant="primary"
                                    size="sm"
                                >
                                    {t('general.button.next')}
                                </Button>
                                <Button
                                    onSubmit={e => openSubmitConfirmModal(e)}
                                    type="submit"
                                    className={`${campaign?.offers?.length || activeTabModal !== 4 ? 'd-none' : ''}`}
                                    variant={isCharity ? 'primary-dark' : 'primary'}
                                    size="sm"
                                >
                                    {t('general.button.submit_offer')}
                                </Button>
                                <Button
                                    type="submit"
                                    className={`${
                                        canCounter &&
                                        isEditable &&
                                        activeTabModal === 4 &&
                                        offerModalStatus === 'negotiate'
                                            ? ''
                                            : 'd-none'
                                    }`}
                                    variant="primary"
                                    size="sm"
                                >
                                    {t('general.button.submit')}
                                </Button>
                                <Button
                                    className={`${
                                        canCounter &&
                                        activeTabModal === 4 &&
                                        offerModalStatus === 'preview' &&
                                        (!campaign?.offers[0]?.isRequested ||
                                            campaign?.company?.isFirstOfferAcceptEnabled)
                                            ? ''
                                            : 'd-none'
                                    } ml-2`}
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setShowAcceptModal(true)}
                                >
                                    {t('general.button.accept')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Modal.Footer>
            </form>
            <InfluencerAcceptOfferModal
                show={showAcceptModal}
                closeModal={closeAcceptModal}
                nbPosts={getNumberPosts(campaign?.offerPlaceholder?.totalPlatforms)}
                grandTotal={grandTotalSum}
                action={acceptOffer}
            />
            <DeclineOfferModal
                date={formatDate(campaign?.offers[0]?.createdAt ?? '')}
                declineOffer={declineOffer}
                show={showDeclineModal}
                closeModal={closeDeclineModal}
                setDeclineReason={setDeclineReasons}
            />
            <ConfirmSendingOfferModal
                action={campaign?.offers?.length ? sendCounterOfferRequest : sendInitialOfferRequest}
                grandTotal={grandTotalSum}
                disableButton={disableButton}
                show={showSubmitModal}
                nbPosts={initialOffer.isAddonsOnly ? 0 : getNumberContentPieces(initialOffer.offerItems)}
                isInfluencer={true}
                closeModal={closeSubmitModal}
            />
        </Modal>
    );
}
